import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Dataset from "datasets/types/interfaces/dataset";
import { Check, ChevronDown, ChevronsUpDown, Folder } from "lucide-react";
import { mutate } from "swr";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { RadioGroup, RadioGroupIndicator } from "~/components/ui/radio-group";
import useToast from "~/components/ui/toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { EmptyConversationView } from "~/pages/manifold-nextgen/components/conversations/EmptyConversationView";
import { Composer } from "~/pages/manifold-nextgen/components/conversations/composer/Composer";
import { useConnectors } from "~/pages/manifold-nextgen/components/conversations/connectors/useConnectors";
import { nextgenConversationPath } from "~/pages/manifold-nextgen/paths";
import { Agent, AgentStatus } from "~/services/agents";
import { createProjectConversation, projectConversationsPath } from "~/services/projects";
import { useProjectContext } from "./useProjectContext";

interface AgentListProps {
  agents: Agent[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const AgentList = ({ agents, selectedId, onSelect }: AgentListProps) => (
  <RadioGroup value={selectedId ?? ""} onValueChange={onSelect} className="block">
    <ul className="max-h-80 divide-y divide-border overflow-y-auto rounded-lg">
      {agents.map((agent) => (
        <li key={agent.id}>
          <label className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-accent/50">
            <RadioGroupIndicator value={agent.id} />
            <AgentAvatar id={agent.id} name={agent.name} size={32} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-foreground">{agent.display_name}</p>
              {agent.description && (
                <p className="truncate text-xs text-muted-foreground">{agent.description}</p>
              )}
            </div>
          </label>
        </li>
      ))}
    </ul>
  </RadioGroup>
);

interface DatasetChipProps {
  datasets: Dataset[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

// Compact dataset selector rendered as a pill under the agent header. Shown
// only when the project has more than one dataset; a single dataset renders as
// a static label instead.
const DatasetChip = ({ datasets, selectedId, onSelect }: DatasetChipProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = datasets.find((d) => d.id === selectedId);
  const filtered = query
    ? datasets.filter((d) => d.title.toLowerCase().includes(query.toLowerCase()))
    : datasets;

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Switch dataset"
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent/50 hover:text-foreground"
        >
          <Folder className="size-3.5 shrink-0" aria-hidden="true" />
          <span className="max-w-[14rem] truncate">{selected?.title ?? "Select a dataset…"}</span>
          <ChevronsUpDown className="size-3 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="center">
        <Command>
          <CommandInput placeholder="Search datasets…" onValueChange={setQuery} />
          <CommandList>
            {filtered.length === 0 && <CommandEmpty>No datasets found.</CommandEmpty>}
            {filtered.map((dataset) => (
              <CommandItem
                key={dataset.id}
                value={dataset.title}
                className="gap-2"
                onSelect={() => {
                  onSelect(dataset.id);
                  setOpen(false);
                  setQuery("");
                }}
              >
                <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                <span className="truncate">{dataset.title}</span>
                <Check
                  className={cn(
                    "ml-auto size-4 shrink-0",
                    selectedId === dataset.id ? "opacity-100" : "opacity-0"
                  )}
                />
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// "New Session" splash for a configured project. Mirrors the empty-conversation
// layout — large agent avatar, starter prompts, centered composer — so starting
// a session is just: pick an agent, type, and send. The conversation is created
// lazily on that first submit; the typed text rides in as its first message.
export const NewSessionPicker = () => {
  const { projectId, attachedAgents, attachedAgentsWithDetails, attachedDatasets } =
    useProjectContext();
  const navigate = useNavigate();
  const { showError } = useToast();

  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [agentPopoverOpen, setAgentPopoverOpen] = useState(false);
  const [starterPromptValue, setStarterPromptValue] = useState<string | undefined>(undefined);

  // Only ENABLED agents can start a new session. DISABLED and soft-DELETED
  // agents still surface their historic conversations in the sidebar, but
  // neither may be picked here to begin a fresh one.
  const enabledAgents = useMemo(
    () => (attachedAgents ?? []).filter((a) => a.status === AgentStatus.ENABLED),
    [attachedAgents]
  );

  // Effective selections: an explicit pick when valid, otherwise the first
  // available. Deriving the default (rather than syncing it through an effect)
  // means the right agent/dataset is chosen on the first paint — no flash of
  // the "no agents" fallback before an effect catches up — and a selection that
  // is detached falls back automatically.
  const datasets = useMemo(() => attachedDatasets ?? [], [attachedDatasets]);
  const effectiveAgentId =
    enabledAgents.find((a) => a.id === selectedAgentId)?.id ?? enabledAgents[0]?.id ?? null;
  const effectiveDatasetId =
    datasets.find((d) => d.id === selectedDatasetId)?.id ?? datasets[0]?.id ?? null;

  const selectedAgent = enabledAgents.find((a) => a.id === effectiveAgentId) ?? null;
  const selectedDataset = datasets.find((d) => d.id === effectiveDatasetId) ?? null;
  const starterPrompts =
    attachedAgentsWithDetails?.find((a) => a.id === effectiveAgentId)?.starter_prompts ?? [];

  // Connector selection is keyed by `projectId:agentId` and persisted (the
  // toggles in localStorage, the guidelines acceptance on the backend), so a
  // choice made here on the splash carries into the conversation we create and
  // is forwarded on its first turn — no need to thread it through navigation.
  const { connectors, selectedConnectorNames, toggleConnector, mcpGuidelines } = useConnectors(
    effectiveAgentId,
    projectId
  );

  // A ref, not `isPending`, gates re-entry: two Enter presses in the same tick
  // both read the stale pre-render `isPending`, so a state flag alone can't stop
  // a double create. `isPending` still drives the composer's disabled state.
  const startingRef = useRef(false);

  const handleStart = async (text: string) => {
    if (startingRef.current) {
      return;
    }
    // A conversation needs both. The mode lock can strand the splash in session
    // mode after every dataset is detached, so surface the gap instead of
    // letting Enter quietly do nothing.
    if (!effectiveAgentId || !effectiveDatasetId) {
      showError("This project needs an agent and a dataset before you can start a session.");
      return;
    }
    startingRef.current = true;
    setIsPending(true);
    try {
      const conversation = await createProjectConversation(projectId, {
        agent_id: effectiveAgentId,
        dataset_id: effectiveDatasetId,
        // Snapshot the dataset's title so the composer's folder indicator has a
        // label for this conversation's actual scope.
        dataset_name: selectedDataset?.title,
        user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      await mutate(projectConversationsPath(projectId));
      // `isNew` shows the centered empty-state skeleton while /connect loads;
      // `initialMessage` is sent as the conversation's first turn on mount.
      navigate(nextgenConversationPath(projectId, conversation.id), {
        state: { isNew: true, initialMessage: text },
      });
    } catch {
      showError("Failed to start session. Please try again.");
      startingRef.current = false;
      setIsPending(false);
    }
  };

  // No ENABLED agent to start a session with (all disabled/deleted). This is a
  // real empty state, distinct from "an agent exists but isn't selected yet" —
  // gating only on the count avoids flashing this while a default resolves.
  if (enabledAgents.length === 0 || !selectedAgent) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold">No agents available</h1>
          <p className="text-sm text-muted-foreground">
            Every agent on this project is disabled. Ask a project owner to enable one in Settings.
          </p>
        </div>
      </div>
    );
  }

  const swapAvatar = enabledAgents.length > 1 && (
    <Popover open={agentPopoverOpen} onOpenChange={setAgentPopoverOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Switch agent"
              className="group relative cursor-pointer rounded-full outline-none transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-ring"
            >
              <AgentAvatar id={selectedAgent.id} name={selectedAgent.name} size={80} />
              <span className="absolute bottom-0 right-0 flex size-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground shadow-sm transition-all group-hover:border-primary group-hover:text-foreground group-data-[state=open]:rotate-180">
                <ChevronDown className="size-3.5" />
              </span>
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">Switch agent</TooltipContent>
      </Tooltip>
      <PopoverContent align="center" className="w-72 p-0">
        <AgentList
          agents={enabledAgents}
          selectedId={effectiveAgentId}
          onSelect={(id) => {
            setSelectedAgentId(id);
            setAgentPopoverOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );

  const datasetChip =
    selectedDataset &&
    (datasets.length > 1 ? (
      <DatasetChip
        datasets={datasets}
        selectedId={effectiveDatasetId}
        onSelect={setSelectedDatasetId}
      />
    ) : (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs text-muted-foreground">
        <Folder className="size-3.5 shrink-0" aria-hidden="true" />
        <span className="max-w-[14rem] truncate">{selectedDataset.title}</span>
      </span>
    ));

  return (
    <EmptyConversationView
      agent={selectedAgent}
      avatar={swapAvatar || undefined}
      headerExtras={datasetChip}
      starterPrompts={starterPrompts}
      onPromptClick={setStarterPromptValue}
      anchorTop
    >
      <Composer
        onSubmit={handleStart}
        disabled={isPending}
        conversationAgent={selectedAgent}
        starterPromptValue={starterPromptValue}
        connectors={connectors}
        enabledConnectorNames={selectedConnectorNames}
        onToggleConnector={toggleConnector}
        mcpGuidelines={mcpGuidelines}
      />
    </EmptyConversationView>
  );
};
